import type { ReactNode } from 'react';
import type React from 'react';

import { Button as HeroButton } from '@heroui/react';

import { cn } from '@/shared/utils';

type HeroVariant = 'primary' | 'secondary' | 'danger' | 'outline' | 'ghost';
type CompositeVariant = 'danger-outline' | 'primary-outline' | 'primary-ghost';

export interface ButtonProps {
  variant?: HeroVariant | CompositeVariant;
  size?: 'sm' | 'md' | 'lg';
  isLoading?: boolean;
  children: ReactNode;
  disabled?: boolean;
  className?: string;
  type?: 'button' | 'submit' | 'reset';
  onClick?: (() => void) | undefined;
}

const COMPOSITE_VARIANTS: Record<CompositeVariant, { hero: HeroVariant; className: string }> = {
  'danger-outline': {
    hero: 'ghost',
    className: 'text-destructive border border-destructive/40 hover:bg-destructive/10',
  },
  'primary-outline': {
    hero: 'ghost',
    className: 'border border-primary text-primary hover:bg-primary/10',
  },
  'primary-ghost': {
    hero: 'ghost',
    className: 'text-primary hover:bg-primary/10',
  },
};

function isCompositeVariant(variant: HeroVariant | CompositeVariant): variant is CompositeVariant {
  return variant in COMPOSITE_VARIANTS;
}

function resolveVariant(variant: HeroVariant | CompositeVariant): {
  hero: HeroVariant;
  colorClassName?: string;
} {
  if (isCompositeVariant(variant)) {
    const composite = COMPOSITE_VARIANTS[variant];
    return { hero: composite.hero, colorClassName: composite.className };
  }
  if (variant === 'secondary') return { hero: 'secondary', colorClassName: 'bg-surface' };
  return { hero: variant };
}

function LoaderSpinner(): React.JSX.Element {
  return (
    <svg
      className="animate-spin h-4 w-4"
      xmlns="http://www.w3.org/2000/svg"
      fill="none"
      viewBox="0 0 24 24"
      aria-hidden="true"
    >
      <title>Loading</title>
      <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4" />
      <path
        className="opacity-75"
        fill="currentColor"
        d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"
      />
    </svg>
  );
}

export function Button({
  variant = 'primary',
  size = 'md',
  isLoading = false,
  children,
  disabled,
  className,
  type = 'button',
  onClick,
}: Readonly<ButtonProps>): React.JSX.Element {
  const { hero, colorClassName } = resolveVariant(variant);
  return (
    <HeroButton
      variant={hero}
      size={size}
      isDisabled={disabled ?? false}
      isPending={isLoading}
      className={cn(colorClassName, className)}
      type={type}
      {...(onClick && { onPress: onClick })}
    >
      {isLoading ? (
        <span className="flex items-center gap-2">
          <LoaderSpinner />
          {children}
        </span>
      ) : (
        children
      )}
    </HeroButton>
  );
}
